import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card,
  CardContent,
  Avatar,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Fade,
  Slide,
  Divider,
  Container
} from "@mui/material";
import {
  PeopleAlt as PeopleAltIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  TableChart as TableChartIcon,
  PersonAdd as PersonAddIcon,
  TrendingUp,
  TrendingDown,
  Notifications,
  Settings,
  Refresh,
  Analytics,
  Security,
  Speed
} from "@mui/icons-material";
import Navbar from "../../components/AdminNavebar";
import { useNavigate } from "react-router-dom";
import { getData } from "../../services/Request";

export default function Admin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalProfiles: 0,
    recentActivity: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      // Fetch user statistics
      const userRes = await getData('api/auth/allUsers?page=1&limit=1');
      setStats(prev => ({
        ...prev,
        totalUsers: userRes.totalUsers || 0,
        activeUsers: userRes.users?.filter(user => user.status).length || 0
      }));
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced Stat Card Component
  const StatCard = ({ icon, title, subtitle, value, color, path, trend, trendValue }) => (
    <Fade in timeout={800}>
      <Card
        onClick={() => navigate(path)}
        sx={{
          background: `linear-gradient(135deg, ${color.start} 0%, ${color.end} 100%)`,
          color: "white",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          cursor: "pointer",
          transition: "all 0.3s ease-in-out",
          position: "relative",
          overflow: "hidden",
          "&:hover": { 
            transform: "translateY(-8px) scale(1.02)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
            opacity: 0,
            transition: "opacity 0.3s ease-in-out"
          },
          "&:hover::before": {
            opacity: 1
          }
        }}
      >
        <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Avatar sx={{ 
              bgcolor: "rgba(255,255,255,0.2)", 
              width: 60, 
              height: 60,
              backdropFilter: "blur(10px)"
            }}>
              {icon}
            </Avatar>
            {trend && (
              <Chip
                icon={trend === 'up' ? <TrendingUp /> : <TrendingDown />}
                label={`${trendValue}%`}
                size="small"
                sx={{
                  bgcolor: trend === 'up' ? "rgba(76, 175, 80, 0.2)" : "rgba(244, 67, 54, 0.2)",
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.3)"
                }}
              />
            )}
          </Box>
          
          <Typography variant="h3" sx={{ fontWeight: "bold", mb: 1 }}>
            {loading ? "..." : value}
          </Typography>
          
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            {title}
          </Typography>
          
          {subtitle && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Fade>
  );

  // Quick Action Card
  const QuickActionCard = ({ icon, title, description, path, color }) => (
    <Slide direction="up" in timeout={1000}>
      <Paper
        onClick={() => navigate(path)}
        sx={{
          p: 3,
          borderRadius: 3,
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,0.2)",
          cursor: "pointer",
          transition: "all 0.3s ease-in-out",
          "&:hover": { 
            transform: "translateY(-4px)",
            boxShadow: "0 12px 24px rgba(0,0,0,0.15)"
          }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ 
            bgcolor: color, 
            width: 50, 
            height: 50 
          }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    }}>
      <Navbar />

      <Box sx={{ flexGrow: 1, p: 4 }}>
        <Container maxWidth="xl">
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: "white", 
                fontWeight: "bold", 
                mb: 1,
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
              }}
            >
              Admin Dashboard
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ color: "rgba(255,255,255,0.8)" }}
            >
              Welcome back! Here's what's happening with your system.
            </Typography>
          </Box>

          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<PeopleAltIcon sx={{ fontSize: 30, color: "white" }} />}
                title="Total Users"
                subtitle="All registered users"
                value={stats.totalUsers}
                color={{ start: "#4CAF50", end: "#45a049" }}
                // path="/admin/UsersTable"
                trend="up"
                trendValue="12"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<PersonIcon sx={{ fontSize: 30, color: "white" }} />}
                title="Active Users"
                subtitle="Currently active"
                value={stats.activeUsers}
                color={{ start: "#2196F3", end: "#1976D2" }}
                trend="up"
                trendValue="8"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<TableChartIcon sx={{ fontSize: 30, color: "white" }} />}
                title="Total Profiles"
                subtitle="User profiles created"
                value={stats.totalProfiles || 0}
                color={{ start: "#FF9800", end: "#F57C00" }}
                // path="/ProfileTable"
                trend="up"
                trendValue="15"
              />
            </Grid>

            {/* <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Analytics sx={{ fontSize: 30, color: "white" }} />}
                title="System Health"
                subtitle="Overall performance"
                value="98%"
                color={{ start: "#9C27B0", end: "#7B1FA2" }}
                path="/Dashboard"
                trend="up"
                trendValue="2"
              />
            </Grid> */}
          </Grid>

          {/* Quick Actions Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: "white", 
                fontWeight: "bold", 
                mb: 3,
                textShadow: "1px 1px 2px rgba(0,0,0,0.3)"
              }}
            >
              Quick Actions
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <QuickActionCard
                  icon={<PeopleAltIcon sx={{ fontSize: 25, color: "white" }} />}
                  title="Manage Users"
                  description="View and manage all user accounts"
                  path="/admin/UsersTable"
                  color="#4CAF50"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <QuickActionCard
                  icon={<PersonAddIcon sx={{ fontSize: 25, color: "white" }} />}
                  title="Add New Profile"
                  description="Create a new user account"
                  path="/admin/addProfile"
                  color="#2196F3"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <QuickActionCard
                  icon={<TableChartIcon sx={{ fontSize: 25, color: "white" }} />}
                  title="View Profiles"
                  description="Browse and edit user profiles"
                  path="/admin/ProfileTable"
                  color="#FF9800"
                />
              </Grid>

              {/* <Grid item xs={12} sm={6} md={3}>
                <QuickActionCard
                  icon={<Settings sx={{ fontSize: 25, color: "white" }} />}
                  title="System Settings"
                  description="Configure system preferences"
                  path="/Settings"
                  color="#9C27B0"
                />
              </Grid> */}
            </Grid>
          </Box>

          {/* System Status Section */}
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                color: "white", 
                fontWeight: "bold", 
                mb: 3,
                textShadow: "1px 1px 2px rgba(0,0,0,0.3)"
              }}
            >
              System Status
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  p: 3,
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: "#4CAF50" }}>
                      <Speed />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Performance Metrics
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">Server Response Time</Typography>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>98ms</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={98} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: "rgba(0,0,0,0.1)",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "#4CAF50"
                        }
                      }} 
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">Database Performance</Typography>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>95%</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={95} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: "rgba(0,0,0,0.1)",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "#2196F3"
                        }
                      }} 
                    />
                  </Box>

                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">System Uptime</Typography>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>99.9%</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={99.9} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: "rgba(0,0,0,0.1)",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "#FF9800"
                        }
                      }} 
                    />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{
                  p: 3,
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: "#2196F3" }}>
                      <Security />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Security Status
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Chip 
                        label="SSL Certificate" 
                        color="success" 
                        size="small"
                        icon={<Security />}
                      />
                      <Typography variant="body2">Valid until 2024-12-31</Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Chip 
                        label="Firewall" 
                        color="success" 
                        size="small"
                        icon={<Security />}
                      />
                      <Typography variant="body2">Active and monitoring</Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Chip 
                        label="Backup" 
                        color="success" 
                        size="small"
                        icon={<Security />}
                      />
                      <Typography variant="body2">Last backup: 2 hours ago</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
